<template>
  <div class="dropdown relative" :class="rootClasses">
    <div v-if="!inline" ref="trigger" role="button" class="dropdown-trigger relative flex items-center focus:outline-none" aria-haspopup="true" @click="toggle">
      <template v-if="$slots['activator']">
        <slot name="activator" />
      </template>
      <d-btn v-else v-text="text" />
    </div>

    <transition :name="animation">
      <div v-if="isMobileModal" v-show="isActive" class="background" :aria-hidden="!isActive" />
    </transition>
    <transition :name="animation">
      <div v-if="(!disabled && (isActive || hoverable)) || inline" ref="dropdownMenu" :class="[dropdownMenuClasses]" class="absolute z-50 mt-2 bg-white rounded-lg border shadow-md py-2" :aria-hidden="!isActive" :role="ariaRoleMenu">
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
  import DBtn from '../DButton/DButton.vue';

  /**
  * @displayName Dropdown
  */
  export default {
    name: 'DDropdown',
    status: 'prototype',
    release: '0.1.7',
    components: {
      DBtn,
    },

    props: {
      value: {
        type: [String, Number, Boolean, Object, Array, Symbol, Function],
        default: null,
      },
      text: {
        type: String,
        default: 'Button',
      },
      disabled: Boolean,
      hoverable: Boolean,
      inline: Boolean,

      /**
       * Position Alignment
       */
      position: {
        type: String,
        default: 'right',
        validator: value => [null, 'right', 'left'].includes(value),
      },
      mobileModal: {
        type: Boolean,
        default: true,
      },

      /**
       * Role attribute to be passed to list container for better accessibility. Use menu only in situations where your dropdown is related to navigation menus.
       */
      ariaRole: {
        type: String,
        default: 'list',
        validator: value => [null, 'menu', 'list'].includes(value),
      },
      animation: {
        type: String,
        default: 'ease-quad',
      },
    },
    data() {
      return {
        selected: this.value,
        isActive: false,
        _isDropdown: true, // Used internally by DropdownItem
      };
    },
    computed: {
      rootClasses() {
        return [{
          'is-disabled': this.disabled,
          'is-hoverable': this.hoverable,
          'is-inline': this.inline,
          'is-active': this.isActive || this.inline,
          'is-mobile-modal': this.isMobileModal,
        }];
      },
      dropdownMenuClasses() {
        return [
          [`${this.position}-0`],
          [`origin-top-${this.position}`],
        ];
      },
      isMobileModal() {
        return this.mobileModal && !this.inline && !this.hoverable;
      },
      ariaRoleMenu() {
        return this.ariaRole === 'menu' || this.ariaRole === 'list' ? this.ariaRole : null;
      },
    },
    watch: {
      /**
       * When v-model is changed set the new selected item.
       */
      value(value) {
        this.selected = value;
      },

      /**
       * Emit event when isActive value is changed.
       */
      isActive(value) {
        this.$emit('active-change', value);
      },
    },
    created() {
      if (typeof window !== 'undefined') {
        document.addEventListener('click', this.clickedOutside);
      }
    },
    beforeDestroy() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('click', this.clickedOutside);
      }
    },
    methods: {
      /**
       * Click listener from DropdownItem.
       *   1. Set new selected item.
       *   2. Emit input event to update the user v-model.
       *   3. Close the dropdown.
       */
      selectItem(value) {
        if (this.selected !== value) {
          this.$emit('change', value);
          this.selected = value;
        }
        this.$emit('input', value);
        this.isActive = false;
      },

      /**
       * White-listed items to not close when clicked.
       */
      isInWhiteList(el) {
        if (el === this.$refs.dropdownMenu) return true;
        if (el === this.$refs.trigger) return true;
        // All chidren from dropdown
        if (this.$refs.dropdownMenu !== undefined) {
          const children = this.$refs.dropdownMenu.querySelectorAll('*');
          for (const child of children) {
            if (el === child) {
              return true;
            }
          }
        }
        // All children from trigger
        if (this.$refs.trigger !== undefined) {
          const children = this.$refs.trigger.querySelectorAll('*');
          for (const child of children) {
            if (el === child) {
              return true;
            }
          }
        }

        return false;
      },

      /**
       * Close dropdown if clicked outside.
       */
      clickedOutside(event) {
        if (this.inline) return;

        if (!this.isInWhiteList(event.target)) this.isActive = false;
      },

      /**
       * Toggle dropdown if it's not disabled.
       */
      toggle() {
        if (this.disabled || this.hoverable) return;

        if (!this.isActive) {
          // if not active, toggle after clickOutside event
          // this fixes toggling programmatic
          this.$nextTick(() => { this.isActive = !this.isActive; });
        } else {
          this.isActive = !this.isActive;
        }
      },
    },
  };
</script>